import React from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { MdCheckCircle, MdInfo, MdSubject, MdTitle } from "react-icons/md";

import { useAuth } from "contexts/AuthContext";
import { isSubscribed } from "utils/subscriptions.utils";
import { generateReportUrl } from "utils/report.utils";
import { RootState } from "redux/reducers";
import { NamedReport } from "types/search";

import ModeSwitch, { SearchMode } from "./ModeSwitch";

// The answer from /search/suggest
export type Suggestions = {
  query: string;
  mode: SearchMode;
  correctedQuery: string | null;
  suggestedQuery: string | null;
  totals: { reports: number; chapters: number; matches: number; named: number };
  reports: {
    report_id: number;
    report_name: string;
    report_year: number;
    category_name: string;
    chapters: number;
    matches: number;
    topChapters: { chapter_number: number; title: string; matches: number }[];
  }[];
  // The first reports whose name matches
  namedReports: NamedReport[];
  // Exact only: how many reports Fuzzy finds when Exact finds none
  fuzzyReports: number | null;
};

type SearchDropdownProps = {
  suggestions: Suggestions;
  mode: SearchMode;
  setMode: (mode: SearchMode) => void;
  box: { left: number; width: number };
  onSuggestion: (query: string) => void;
  onNavigate: () => void;
};

// Also used by the results page, so both show reports the same way
export const formatCount = (count: number, word: string, plural = `${word}s`) =>
  `${count.toLocaleString("en-US")} ${count === 1 ? word : plural}`;

const escapeRegExp = (text: string) =>
  text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Bolds the search text where the title has all of it as typed, the first time
// it appears, as the results page did before the dropdown (renderTitle)
export const highlight = (title: string, term: string) => {
  if (!term) return title;

  const match = new RegExp(escapeRegExp(term), "i").exec(title);
  if (!match) return title;

  const end = match.index + match[0].length;
  return [
    title.slice(0, match.index),
    <strong key="match">{match[0]}</strong>,
    title.slice(end),
  ];
};

// Design 3a: the top reports for what's typed, with their match counts
const SearchDropdown: React.FC<SearchDropdownProps> = ({
  suggestions,
  mode,
  setMode,
  box,
  onSuggestion,
  onNavigate,
}) => {
  const { currentUser } = useAuth();
  const subscriptions = useSelector((state: RootState) => state.subscriptions);
  const {
    query,
    correctedQuery,
    suggestedQuery,
    totals,
    reports,
    fuzzyReports,
  } = suggestions;
  const namedReports = suggestions.namedReports || [];
  const term = correctedQuery || query;
  const exact = suggestions.mode === "exact";
  const subscribed = (reportId: number) =>
    isSubscribed(subscriptions, reportId, currentUser, "active");

  return (
    <Container style={{ left: box.left, width: box.width }}>
      <HeaderRow>
        <HeaderLeft>
          <ModeLabel>{exact ? "Exact match" : "Fuzzy match"}</ModeLabel>
          <HeaderSwitch mode={mode} setMode={setMode} light />
          <QueryText>for “{query}”</QueryText>
        </HeaderLeft>
        <span>
          {reports.length
            ? `${formatCount(totals.reports, "report")} · ${formatCount(
                totals.matches,
                "match",
                "matches"
              )}`
            : namedReports.length
            ? "No chapter matches"
            : "No results"}
        </span>
      </HeaderRow>

      {correctedQuery && (
        <Note>
          <MdInfo aria-hidden="true" />
          <span>
            No reports matched “{query}”, so these are the results for “
            {correctedQuery}”.
          </span>
        </Note>
      )}
      {suggestedQuery && (
        <Note>
          <MdInfo aria-hidden="true" />
          <span>
            Did you mean{" "}
            <NoteButton type="button" onClick={() => onSuggestion(suggestedQuery)}>
              {suggestedQuery}
            </NoteButton>
            ?
          </span>
        </Note>
      )}

      <Rows>
        {namedReports.length > 0 && (
          <Named>
            <GroupLabel>
              <MdTitle aria-hidden="true" />
              <span>
                Found in report names
                {totals.named > namedReports.length &&
                  ` · ${formatCount(totals.named, "report")}`}
              </span>
            </GroupLabel>
            {namedReports.map((report) => (
              <NamedRow
                key={report.report_id}
                to={`/reports/${report.report_id}/${generateReportUrl(
                  report.report_name
                )}/1`}
                title={report.report_name}
                onClick={onNavigate}
              >
                <RowTitle>{highlight(report.report_name, term)}</RowTitle>
                <ReportMeta
                  report={report}
                  subscribed={subscribed(report.report_id)}
                />
              </NamedRow>
            ))}
          </Named>
        )}

        {!reports.length ? (
          <Empty>
            <EmptyTitle>
              {namedReports.length
                ? `No chapters ${exact ? "have" : "match"} “${query}”`
                : exact
                ? `No exact matches for “${query}”`
                : `No reports match “${query}”`}
            </EmptyTitle>
            {exact && fuzzyReports ? (
              <>
                <EmptyHint>
                  Fuzzy finds {formatCount(fuzzyReports, "report")} with close
                  matches.
                </EmptyHint>
                <FuzzyButton type="button" onClick={() => setMode("fuzzy")}>
                  Search with Fuzzy
                </FuzzyButton>
              </>
            ) : (
              <EmptyHint>Try other or fewer words.</EmptyHint>
            )}
          </Empty>
        ) : (
          <>
            {namedReports.length > 0 && (
              <GroupLabel>
                <MdSubject aria-hidden="true" />
                <span>Found in chapters</span>
              </GroupLabel>
            )}
            {reports.map((report) => {
              const top = report.topChapters[0];
              const url = `/reports/${report.report_id}/${generateReportUrl(
                report.report_name
              )}/${top ? top.chapter_number : 1}?term=${encodeURIComponent(
                query
              )}&mode=${suggestions.mode}`;
              const more = report.chapters - report.topChapters.length;

              return (
                <Row
                  key={report.report_id}
                  to={url}
                  title={report.report_name}
                  onClick={onNavigate}
                >
                  <RowTop>
                    <RowText>
                      <RowTitle>{highlight(report.report_name, term)}</RowTitle>
                      <ReportMeta
                        report={report}
                        subscribed={subscribed(report.report_id)}
                      />
                    </RowText>
                    <RowTotal>
                      <strong>{report.matches.toLocaleString("en-US")}</strong>
                      <span>{report.matches === 1 ? "match" : "matches"}</span>
                    </RowTotal>
                  </RowTop>
                  <RowChapters>
                    <span>Found in {formatCount(report.chapters, "chapter")}</span>
                    {report.topChapters.map((chapter) => (
                      <Chip key={chapter.chapter_number}>
                        <ChipTitle>
                          {chapter.title || `Chapter ${chapter.chapter_number}`}
                        </ChipTitle>
                        <strong>{chapter.matches.toLocaleString("en-US")}</strong>
                      </Chip>
                    ))}
                    {more > 0 && <More>+{more} more</More>}
                  </RowChapters>
                </Row>
              );
            })}
          </>
        )}
      </Rows>

      {(reports.length > 0 || namedReports.length > 0) && (
        <Footer type="submit">
          <span>
            {reports.length
              ? `See all ${formatCount(totals.reports, "report")}`
              : "See results"}
          </span>
          <EnterHint>
            or press <Key>Enter</Key>
          </EnterHint>
        </Footer>
      )}
    </Container>
  );
};

// A report's category, year and whether the company subscribes to it
const ReportMeta: React.FC<{
  report: { category_name: string; report_year: number };
  subscribed: boolean;
}> = ({ report, subscribed }) => (
  <RowMeta>
    <span>
      {report.category_name} · {report.report_year}
    </span>
    <span aria-hidden="true">·</span>
    {subscribed ? (
      <Subscribed>
        <MdCheckCircle aria-hidden="true" />
        Subscribed
      </Subscribed>
    ) : (
      <span>Not subscribed</span>
    )}
  </RowMeta>
);

const Container = styled.div`
  position: absolute;
  top: calc(100% + 12px);
  left: 0;
  overflow: hidden;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 7.56px 24.19px rgba(0, 0, 0, 0.28);
  color: #000;
  font-size: 15.12px;
  text-align: left;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 2px 12px;
  padding: 11px 16px;
  border-bottom: 1px solid ${(props) => props.theme.colors.grey};
  font-size: 13.44px;
  line-height: 18px;
  color: #5b6573;
`;

const HeaderLeft = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const ModeLabel = styled.strong`
  flex: none;
  font-weight: 600;
  color: #14202e;

  /* From 501 to 900px the switch takes the label's place */
  @media screen and (min-width: ${(props) => props.theme.bp.xs + 1}px) and (max-width: ${(props) => props.theme.bp.md}px) {
    display: none;
  }
`;

const HeaderSwitch = styled(ModeSwitch)`
  display: none;

  @media screen and (min-width: ${(props) => props.theme.bp.xs + 1}px) and (max-width: ${(props) => props.theme.bp.md}px) {
    display: flex;
  }
`;

const QueryText = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Note = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  background-color: #eef3f7;
  border-bottom: 1px solid ${(props) => props.theme.colors.grey};
  font-size: 13.44px;
  line-height: 18px;
  color: ${(props) => props.theme.colors.idataDarkBlue};

  svg {
    flex: none;
    width: 18px;
    height: 18px;
  }
`;

const NoteButton = styled.button`
  padding: 0;
  background: none;
  border: none;
  border-radius: 0;
  color: ${(props) => props.theme.colors.idataDarkBlue};
  font: inherit;
  font-weight: 600;
  text-decoration: underline;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 20px 16px 22px;
`;

const EmptyTitle = styled.div`
  font-size: 15.12px;
  font-weight: 600;
  color: #14202e;
`;

const EmptyHint = styled.div`
  font-size: 13.44px;
  color: #5b6573;
`;

const FuzzyButton = styled.button`
  margin-top: 8px;
  height: 34px;
  padding: 0 16px;
  background-color: transparent;
  border: 1px solid ${(props) => props.theme.colors.idataBlue};
  border-radius: 42px;
  color: ${(props) => props.theme.colors.idataBlue};
  font-family: inherit;
  font-size: 14.28px;
  font-weight: 600;

  &:hover {
    background-color: ${(props) => props.theme.colors.idataBlue};
    color: #fff;
  }
`;

// The name matches and the chapter results scroll together, between the header
// and the footer, so the dropdown stays inside the window
const Rows = styled.div`
  max-height: min(600px, calc(100vh - 220px));
  overflow-y: auto;
`;

const Row = styled(Link)`
  display: block;
  padding: 10px 16px 11px;
  border-bottom: 1px solid #eef0f2;
  color: #14202e;

  /* A blue tint and a bar on the left, so the row the mouse or Tab key is on shows */
  &:hover,
  &:focus-visible {
    background-color: #eaf4fa;
    box-shadow: inset 3px 0 0 ${(props) => props.theme.colors.idataBlue};
    color: #14202e;
    opacity: 1;
    outline: none;
  }
`;

// The reports matched by name, above the chapter results
const Named = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.colors.grey};
`;

const NamedRow = styled(Row)`
  padding: 7px 16px 8px;

  &:last-child {
    border-bottom: none;
  }
`;

// A tinted band with an icon, kept at the top while its group scrolls under it
const GroupLabel = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  background-color: #eef3f7;
  border-bottom: 1px solid ${(props) => props.theme.colors.grey};
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.idataDarkBlue};

  svg {
    flex: none;
    width: 16px;
    height: 16px;
  }
`;

const RowTop = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const RowText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const RowTitle = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 15.12px;
  line-height: 20px;

  ${Row}:hover &,
  ${Row}:focus-visible & {
    color: ${(props) => props.theme.colors.idataBlue};
  }
`;

const RowMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  font-size: 13.44px;
  line-height: 18px;
  color: #5b6573;
`;

const Subscribed = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: ${(props) => props.theme.colors.idataDarkBlue};
  font-weight: 600;

  svg {
    width: 15px;
    height: 15px;
  }
`;

const RowTotal = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  strong {
    font-size: 21px;
    font-weight: 700;
    line-height: 24px;
    color: ${(props) => props.theme.colors.idataDarkBlue};
  }

  span {
    font-size: 12.6px;
    line-height: 16px;
    color: #5b6573;
  }
`;

const RowChapters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 12.6px;
  color: #5b6573;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  height: 24px;
  padding: 0 10px;
  background-color: #eef3f7;
  border-radius: 999px;
  color: #14202e;

  /* white on the hovered row, whose tint is close to the chip's colour */
  ${Row}:hover &,
  ${Row}:focus-visible & {
    background-color: #fff;
  }

  strong {
    color: ${(props) => props.theme.colors.idataDarkBlue};
  }
`;

const ChipTitle = styled.span`
  max-width: 240px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const More = styled.span`
  font-weight: 600;
  color: ${(props) => props.theme.colors.idataDarkBlue};
`;

const Footer = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  background-color: #fff;
  border: none;
  border-radius: 0;
  color: ${(props) => props.theme.colors.idataBlue};
  font-family: inherit;
  font-size: 14.28px;
  font-weight: 600;
  text-align: left;

  &:hover {
    background-color: ${(props) => props.theme.colors.lighterGrey};
  }
`;

const EnterHint = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.6px;
  font-weight: 400;
  color: #5b6573;

  @media screen and (max-width: ${(props) => props.theme.bp.xs}px) {
    display: none;
  }
`;

const Key = styled.span`
  padding: 1px 6px;
  background-color: ${(props) => props.theme.colors.lighterGrey};
  border: 1px solid #cfd4da;
  border-radius: 4px;
  color: ${(props) => props.theme.colors.idataDarkGrey};
  font-size: 12px;
`;

export default SearchDropdown;
